import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Container from '@mui/material/Container';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import SwipeableDrawer from '@mui/material/SwipeableDrawer';
import TextField from '@mui/material/TextField';
import { DropzoneAreaBase } from 'material-ui-dropzone';
import { NON_ERASABLE_DEPARTMENT_ID, ENDPOINT, LIMIT_PAGE } from 'constants/variables';
import { successErrorAction, warningErrorAction } from 'actions/ErrorActions';
import { helpHttp } from 'service/HelpHttp';
import { setLoading } from 'reducers/loadingSlice';
import TableSortingDepartment from 'pages/commonsarticle/departmentSettings/SortingTablesDepartment';
import SectionSettings from 'pages/commonsarticle/departmentSettings/SectionSettings';

import 'css/generalSettings.css';
import 'css/customDialog.css';

const INITIAL_FILTERS = {
  idFrom: '',
  idTo: '',
  codeFrom: '',
  codeTo: '',
  nameesFrom: '',
  nameesTo: '',
  nameenFrom: '',
  nameenTo: '',
};

const EMPTY_DEPARTMENT = {
  id: '',
  code: '',
  namees: '',
  nameen: '',
  filename: '',
  createdate: null,
  datafile: '',
  type: '',
  numTotSections: 0,
  sections: [],
};

const INITIAL_DATA = [EMPTY_DEPARTMENT];

const HEAD_CELLS = [
  { id: 'id', numeric: false, disablePadding: false, label: 'Text_Code', canOrder: true },
  { id: 'code', numeric: false, disablePadding: false, label: 'Text_CodeDepartment', canOrder: true },
  { id: 'namees', numeric: false, disablePadding: false, label: 'Text_NameEs', canOrder: true },
  { id: 'nameen', numeric: false, disablePadding: false, label: 'Text_NameEn', canOrder: true },
  { id: 'image', numeric: false, disablePadding: false, label: 'TextImage', canOrder: false },
  { id: 'sections', numeric: false, disablePadding: false, label: 'Text_Sections', canOrder: false },
];

const INVALID_CHARACTERS_REGEX = /[!$%&|<>?{}~"']+/;
const NUMBER_REGEX = /^\d+$/;
const hasValue = (value) => value !== undefined && value !== null && value !== '';
const normalizeString = (value) => String(value || '').trim().toLowerCase();
const departmentToFileObject = async (department) => {
  if (!department?.filename || !department?.datafile || !department?.type) {
    return [];
  }
  const dataUrl = `data:${department.type};base64,${department.datafile}`;
  const blob = await (await fetch(dataUrl)).blob();
  const file = new File([blob], department.filename, {
    type: department.type,
    lastModified: department.createdate ? new Date(department.createdate).getTime() : Date.now(),
  });

  return [{ data: dataUrl, file }];
};

const buildOffsetRanges = (total) => {
  if (total <= LIMIT_PAGE) {
    return [{ value: 0, label: `1 - ${Number(total || LIMIT_PAGE).toLocaleString('de-DE')}` }];
  }
  const ranges = [];
  for (let offset = 0; offset < total; offset += Number(LIMIT_PAGE)) {
    const from = offset + 1;
    const to = Math.min(offset + Number(LIMIT_PAGE), total);
    ranges.push({
      value: offset,
      label: `${from.toLocaleString('de-DE')} - ${to.toLocaleString('de-DE')}`,
    });
  }
  return ranges;
};

const DepartmentSettings = ({
  setShowPrincipalMenu,
  setShowModalGeneralSettings,
  showDepartmentSettings,
  setShowModalDepartmentSettings,
  setShowModalArticlesSettings,
  isArticle,
  setShowModalPurchasesDeliverySettings,
  isPurchasesDelivery,
}) => {
  const { t, i18n } = useTranslation(['listDataArticles']);
  const dispatch = useDispatch();
  const token = useSelector((state) => state.loginUser.token);
  const roles = useSelector((state) => state.loginUser.roles);
  const deleteCode = useSelector((state) => state.formatDIUser.delete_code);
  const isLoadingData = useSelector((state) => state.loading.isLoading);
  const [dataDepartment, setDataDepartment] = useState(INITIAL_DATA);
  const [formDepartment, setFormDepartment] = useState(EMPTY_DEPARTMENT);
  const [rowEditDepartment, setRowEditDepartment] = useState(EMPTY_DEPARTMENT);
  const [filtersDepartment, setFiltersDepartment] = useState(INITIAL_FILTERS);
  const [applyFiltersDepartment, setApplyFiltersDepartment] = useState(INITIAL_FILTERS);
  const [numTotRegDepartment, setNumTotRegDepartment] = useState(0);
  const [numTotRegFilter, setNumTotRegFilter] = useState(0);
  const [currencyOffset, setCurrencyOffset] = useState(0);
  const [rangeOffset, setRangeOffset] = useState([{}]);
  const [activeOffset, setActiveOffset] = useState(false);
  const [inputTextDepartment, setInputTextDepartment] = useState('');
  const [files, setFiles] = useState([]);
  const [errorsDepartment, setErrorsDepartment] = useState({});
  const [errorsFiltersDepartment, setErrorsFiltersDepartment] = useState({});
  const [isLoadingErrorDepartment, setIsLoadingErrorDepartment] = useState(false);
  const [openCreateDepartment, setOpenCreateDepartment] = useState(false);
  const [openDeleteRowDepartment, setOpenDeleteRowDepartment] = useState(false);
  const [openEditRowDepartment, setOpenEditRowDepartment] = useState(false);
  const [openEditFilterDepartment, setOpenEditFilterDepartment] = useState(false);
  const [openSections, setOpenSections] = useState(false);
  const [refreshData, setRefreshData] = useState(false);
  const [idCodeDelete, setIdCodeDelete] = useState(deleteCode);

  const permissions = useMemo(() => {
    const menus = roles?.[0]?.listmenus || [];
    const hasPermission = (name) => menus.some((menu) => menu.name === name);
    return {
      canCreate: hasPermission('SubMenu_Department_Create'),
      canEdit: hasPermission('SubMenu_Department_Edit'),
      canDelete: hasPermission('SubMenu_Department_Delete'),
      canViewSections: hasPermission('SubMenu_Settings_Section'),
    };
  }, [roles]);

  const resetLocalState = useCallback(() => {
    setDataDepartment(INITIAL_DATA);
    setFormDepartment(EMPTY_DEPARTMENT);
    setRowEditDepartment(EMPTY_DEPARTMENT);
    setFiltersDepartment(INITIAL_FILTERS);
    setApplyFiltersDepartment(INITIAL_FILTERS);
    setNumTotRegDepartment(0);
    setNumTotRegFilter(0);
    setErrorsFiltersDepartment({});
    setErrorsDepartment({});
    setInputTextDepartment('');
    setOpenCreateDepartment(false);
    setOpenDeleteRowDepartment(false);
    setOpenEditRowDepartment(false);
    setOpenEditFilterDepartment(false);
    setIsLoadingErrorDepartment(false);
    setIdCodeDelete(deleteCode);
    setActiveOffset(false);
    setCurrencyOffset(0);
    setRangeOffset([{}]);
    setOpenSections(false);
    setRefreshData(false);
    setFiles([]);
    dispatch(setLoading(false));
  }, [deleteCode, dispatch]);

  const closeModalDepartmentTypeSettings = useCallback(() => {
    resetLocalState();
    setShowPrincipalMenu(true);
    setShowModalGeneralSettings(false);
    setShowModalDepartmentSettings(false);
    setShowModalArticlesSettings(isArticle);
    setShowModalPurchasesDeliverySettings(isPurchasesDelivery);
  }, [
    isArticle,
    isPurchasesDelivery,
    resetLocalState,
    setShowModalArticlesSettings,
    setShowModalDepartmentSettings,
    setShowModalGeneralSettings,
    setShowModalPurchasesDeliverySettings,
    setShowPrincipalMenu,
  ]);

  const validateFilterForm = useCallback(() => {
    const errors = {};

    if (hasValue(filtersDepartment.idFrom) && !NUMBER_REGEX.test(filtersDepartment.idFrom)) {
      errors.idFrom = 'Text_TextNoNumber';
    }

    if (hasValue(filtersDepartment.idTo) && !NUMBER_REGEX.test(filtersDepartment.idTo)) {
      errors.idTo = 'Text_TextNoNumber';
    }

    if (
      hasValue(filtersDepartment.idFrom) &&
      hasValue(filtersDepartment.idTo) &&
      !errors.idFrom &&
      !errors.idTo &&
      Number(filtersDepartment.idTo) < Number(filtersDepartment.idFrom)
    ) {
      errors.idTo = 'Text_TextGreater_Id';
    }

    ['codeFrom', 'codeTo', 'nameesFrom', 'nameesTo', 'nameenFrom', 'nameenTo'].forEach((field) => {
      if (INVALID_CHARACTERS_REGEX.test(filtersDepartment[field])) {
        errors[field] = 'Text_TextNoValid';
      }
    });

    if (hasValue(filtersDepartment.codeTo) && filtersDepartment.codeTo < filtersDepartment.codeFrom) {
      errors.codeTo = 'Text_TextGreater_DepartmentCode';
    }

    if (hasValue(filtersDepartment.nameesTo) && filtersDepartment.nameesTo < filtersDepartment.nameesFrom) {
      errors.nameesTo = 'Text_TextGreater_Namees';
    }

    if (hasValue(filtersDepartment.nameenTo) && filtersDepartment.nameenTo < filtersDepartment.nameenFrom) {
      errors.nameenTo = 'Text_TextGreater_Nameen';
    }

    return errors;
  }, [filtersDepartment]);

  const validateDepartmentForm = useCallback(() => {
    const errors = {};
    const currentId = rowEditDepartment.id;

    const codeExists = dataDepartment.some(
      (item) => normalizeString(item.code) === normalizeString(formDepartment.code) && (!openEditRowDepartment || item.id !== currentId),
    );

    const nameEsExists = dataDepartment.some(
      (item) => normalizeString(item.namees) === normalizeString(formDepartment.namees) && (!openEditRowDepartment || item.id !== currentId),
    );

    const nameEnExists = dataDepartment.some(
      (item) => normalizeString(item.nameen) === normalizeString(formDepartment.nameen) && (!openEditRowDepartment || item.id !== currentId),
    );

    if (!hasValue(formDepartment.code)) {
      errors.code = 'Text_FieldCodeDepartmentRequired';
    } else if (codeExists) {
      errors.code = 'Text_Department_CodeExist';
    }

    if (!hasValue(formDepartment.namees)) {
      errors.namees = 'Text_FieldNameesRequired';
    } else if (nameEsExists) {
      errors.namees = 'Text_Department_NameesExist';
    }

    if (!hasValue(formDepartment.nameen)) {
      errors.nameen = 'Text_FieldNameenRequired';
    } else if (nameEnExists) {
      errors.nameen = 'Text_Department_NameenExist';
    }

    return errors;
  }, [dataDepartment, formDepartment, openEditRowDepartment, rowEditDepartment.id]);

  const filteredDataDepartment = useMemo(() => {
    const search = normalizeString(inputTextDepartment);
    if (!search) {
      return dataDepartment;
    }

    return dataDepartment.filter((item) => {
      const searchable = [item.id, item.code, item.namees, item.nameen].map((value) => String(value || '')).join(' ').toLowerCase();
      return searchable.includes(search);
    });
  }, [dataDepartment, inputTextDepartment]);

  const openCreateOrEditDataDepartment = useCallback(() => {
    setInputTextDepartment('');
    setErrorsDepartment({});
    setFormDepartment(EMPTY_DEPARTMENT);
    setFiles([]);
    setOpenEditRowDepartment(false);
    setOpenCreateDepartment(true);
  }, []);

  const openEditFiltersDepartment = useCallback(() => {
    setFiltersDepartment(applyFiltersDepartment);
    setErrorsFiltersDepartment({});
    setOpenEditFilterDepartment((prev) => !prev);
  }, [applyFiltersDepartment]);

  const handleChangeFilterEditDepartment = useCallback((event) => {
    const { name, value } = event.target;
    setFiltersDepartment((prev) => ({ ...prev, [name]: value }));
  }, []);

  const handleBlurFilterEditDepartment = useCallback(
    (event) => {
      const { name, value } = event.target;
      const nextFilters = { ...filtersDepartment, [name]: value };
      setFiltersDepartment(nextFilters);

      const errors = (() => {
        const localErrors = {};

        if (hasValue(nextFilters.idFrom) && !NUMBER_REGEX.test(nextFilters.idFrom)) {
          localErrors.idFrom = 'Text_TextNoNumber';
        }
        if (hasValue(nextFilters.idTo) && !NUMBER_REGEX.test(nextFilters.idTo)) {
          localErrors.idTo = 'Text_TextNoNumber';
        }
        if (
          hasValue(nextFilters.idFrom) &&
          hasValue(nextFilters.idTo) &&
          !localErrors.idFrom &&
          !localErrors.idTo &&
          Number(nextFilters.idTo) < Number(nextFilters.idFrom)
        ) {
          localErrors.idTo = 'Text_TextGreater_Id';
        }

        ['codeFrom', 'codeTo', 'nameesFrom', 'nameesTo', 'nameenFrom', 'nameenTo'].forEach((field) => {
          if (INVALID_CHARACTERS_REGEX.test(nextFilters[field])) {
            localErrors[field] = 'Text_TextNoValid';
          }
        });

        if (hasValue(nextFilters.codeTo) && nextFilters.codeTo < nextFilters.codeFrom) {
          localErrors.codeTo = 'Text_TextGreater_DepartmentCode';
        }
        if (hasValue(nextFilters.nameesTo) && nextFilters.nameesTo < nextFilters.nameesFrom) {
          localErrors.nameesTo = 'Text_TextGreater_Namees';
        }
        if (hasValue(nextFilters.nameenTo) && nextFilters.nameenTo < nextFilters.nameenFrom) {
          localErrors.nameenTo = 'Text_TextGreater_Nameen';
        }

        return localErrors;
      })();

      setErrorsFiltersDepartment(errors);
    },
    [filtersDepartment],
  );

  const handleSubmitFilterEditDepartment = useCallback(() => {
    const errors = validateFilterForm();
    setErrorsFiltersDepartment(errors);

    if (Object.keys(errors).length > 0) {
      dispatch(warningErrorAction(t('Text_Error_Filter')));
      return;
    }

    setApplyFiltersDepartment(filtersDepartment);
    setCurrencyOffset(0);
    setErrorsFiltersDepartment({});
    setOpenEditFilterDepartment(false);
  }, [dispatch, filtersDepartment, t, validateFilterForm]);

  const handleDeleteFiltersDepartment = useCallback(() => {
    setErrorsFiltersDepartment({});
    setFiltersDepartment(INITIAL_FILTERS);
    setApplyFiltersDepartment(INITIAL_FILTERS);
    setCurrencyOffset(0);
    setOpenEditFilterDepartment(false);
  }, []);

  const toggleDrawerFilterEditDepartment = useCallback(
    (open) => (event) => {
      if (event.type === 'keydown' && (event.key === 'Tab' || event.key === 'Shift')) {
        return;
      }

      if (!open) {
        setErrorsFiltersDepartment({});
        setFiltersDepartment(INITIAL_FILTERS);
        setOpenEditFilterDepartment(false);
      }
    },
    [],
  );

  const handleChangeDepartment = useCallback((event) => {
    const { name, value } = event.target;
    setFormDepartment((prev) => ({ ...prev, [name]: value }));
  }, []);

  const handleBlurDepartment = useCallback(
    (event) => {
      const { name, value } = event.target;
      setFormDepartment((prev) => {
        const updated = { ...prev, [name]: value };
        const currentId = rowEditDepartment.id;
        const errors = {};

        const codeExists = dataDepartment.some(
          (item) => normalizeString(item.code) === normalizeString(updated.code) && (!openEditRowDepartment || item.id !== currentId),
        );
        const nameEsExists = dataDepartment.some(
          (item) => normalizeString(item.namees) === normalizeString(updated.namees) && (!openEditRowDepartment || item.id !== currentId),
        );
        const nameEnExists = dataDepartment.some(
          (item) => normalizeString(item.nameen) === normalizeString(updated.nameen) && (!openEditRowDepartment || item.id !== currentId),
        );

        if (!hasValue(updated.code)) {
          errors.code = 'Text_FieldCodeDepartmentRequired';
        } else if (codeExists) {
          errors.code = 'Text_Department_CodeExist';
        }

        if (!hasValue(updated.namees)) {
          errors.namees = 'Text_FieldNameesRequired';
        } else if (nameEsExists) {
          errors.namees = 'Text_Department_NameesExist';
        }

        if (!hasValue(updated.nameen)) {
          errors.nameen = 'Text_FieldNameenRequired';
        } else if (nameEnExists) {
          errors.nameen = 'Text_Department_NameenExist';
        }

        setErrorsDepartment(errors);
        return updated;
      });
    },
    [dataDepartment, openEditRowDepartment, rowEditDepartment.id],
  );

  const handleAddFileImage = useCallback((newFiles) => {
    setFiles([...newFiles]);
  }, []);

  const handleDeleteFileImage = useCallback((deletedFile) => {
    setFiles((prev) => prev.filter((file) => file !== deletedFile));
  }, []);

  const toBase64 = useCallback(
    (file) =>
      new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = () => resolve(reader.result.split(',')[1]);
        reader.onerror = reject;
      }),
    [],
  );

  const buildUpdatedFiles = useCallback(async () => {
    return Promise.all(
      files.map(async (item, index) => {
        const file = item.file || item;
        const base64 = await toBase64(file);

        return {
          id: index,
          filename: file.name,
          createdate: file.lastModified,
          datafile: base64,
          type: file.type,
        };
      }),
    );
  }, [files, toBase64]);

  const handleCloseDataEditDepartment = useCallback(() => {
    setOpenCreateDepartment(false);
    setOpenEditRowDepartment(false);
    setErrorsDepartment({});
    setFormDepartment(EMPTY_DEPARTMENT);
    setFiles([]);
    setIsLoadingErrorDepartment(false);
    setRefreshData(false);
    dispatch(setLoading(false));
  }, [dispatch]);

  const toggleDrawerDataEditDepartment = useCallback(
    (open) => (event) => {
      if (event.type === 'keydown' && (event.key === 'Tab' || event.key === 'Shift')) {
        return;
      }

      if (!open) {
        handleCloseDataEditDepartment();
      }
    },
    [handleCloseDataEditDepartment],
  );

  const submitDepartmentRequest = useCallback(
    async (payload) => {
      dispatch(setLoading(true));

      try {
        const response = openEditRowDepartment
          ? await helpHttp().put(ENDPOINT, 'department/updatedepartment', token, payload)
          : await helpHttp().post(ENDPOINT, 'department/savedepartment', token, payload);

        if (response.status === 'OK') {
          dispatch(successErrorAction(t('Text_SaveData')));
          setRefreshData(true);
          handleCloseDataEditDepartment();
          return;
        }

        dispatch(warningErrorAction(t(response.cause)));
      } catch (error) {
        dispatch(warningErrorAction(t('Text_IncorrectData')));
      } finally {
        dispatch(setLoading(false));
      }
    },
    [dispatch, handleCloseDataEditDepartment, openEditRowDepartment, t, token],
  );

  const handleSubmitDataEditDepartment = useCallback(async () => {
    const errors = validateDepartmentForm();
    setErrorsDepartment(errors);

    if (Object.keys(errors).length > 0) {
      if (errors.code) dispatch(warningErrorAction(t(errors.code)));
      else if (errors.namees) dispatch(warningErrorAction(t(errors.namees)));
      else if (errors.nameen) dispatch(warningErrorAction(t(errors.nameen)));
      else dispatch(warningErrorAction(t('Text_IncorrectData')));
      return;
    }

    const updatedFiles = files.length > 0 ? await buildUpdatedFiles() : [];
    const firstFile = updatedFiles[0];

    const payload = {
      ...formDepartment,
      filename: firstFile?.filename || '',
      createdate: firstFile?.createdate || new Date(),
      datafile: firstFile?.datafile || '',
      type: firstFile?.type || '',
    };

    setFormDepartment(payload);
    await submitDepartmentRequest(payload);
  }, [buildUpdatedFiles, files.length, formDepartment, submitDepartmentRequest, t, validateDepartmentForm, dispatch]);

  const handleChangeDeleteRowDepartment = useCallback((event) => {
    setIdCodeDelete(event.target.value);
  }, []);

  const handleCancelDeleteRowDepartment = useCallback(() => {
    setErrorsDepartment({});
    setRowEditDepartment(EMPTY_DEPARTMENT);
    setFormDepartment(EMPTY_DEPARTMENT);
    setIdCodeDelete(deleteCode);
    setIsLoadingErrorDepartment(false);
    setOpenDeleteRowDepartment(false);
    dispatch(setLoading(false));
  }, [deleteCode, dispatch]);

  const handleCloseDeleteRowDepartment = useCallback(async () => {
    if (idCodeDelete !== deleteCode) {
      dispatch(warningErrorAction(t('Text_InvalidCode')));
      return;
    }

    if (!rowEditDepartment.id) {
      dispatch(warningErrorAction(t('Text_InvalidId')));
      return;
    }

    dispatch(setLoading(true));
    try {
      const response = await helpHttp().del(ENDPOINT, 'department/deletedepartment', token, rowEditDepartment);

      if (response.status === 'OK') {
        dispatch(successErrorAction(t('Text_ValidCode')));
        dispatch(successErrorAction(t('Text_DeleteField')));
        setRefreshData(true);
      } else {
        dispatch(warningErrorAction(t(response.cause)));
      }
    } catch (error) {
      dispatch(warningErrorAction(t('Text_IncorrectData')));
    } finally {
      dispatch(setLoading(false));
      setIdCodeDelete(deleteCode);
      setOpenDeleteRowDepartment(false);
      handleCloseDataEditDepartment();
    }
  }, [deleteCode, dispatch, handleCloseDataEditDepartment, idCodeDelete, rowEditDepartment, t, token]);

  const inputHandlerDepartment = useCallback((event) => {
    setInputTextDepartment(event.target.value.toLowerCase());
  }, []);

  useEffect(() => {
    const handleKeyPress = (event) => {
      if (event.key === 'Escape' && showDepartmentSettings) {
        closeModalDepartmentTypeSettings();
      }
    };

    document.addEventListener('keydown', handleKeyPress);
    return () => document.removeEventListener('keydown', handleKeyPress);
  }, [closeModalDepartmentTypeSettings, showDepartmentSettings]);

  useEffect(() => {
    setActiveOffset(numTotRegFilter > LIMIT_PAGE);
    setRangeOffset(numTotRegFilter > LIMIT_PAGE ? buildOffsetRanges(numTotRegFilter) : [{}]);

    if (numTotRegFilter <= LIMIT_PAGE) {
      setCurrencyOffset(0);
    }
  }, [numTotRegFilter]);

  useEffect(() => {
    let ignore = false;

    const fetchAll = async () => {
      if (!showDepartmentSettings && !refreshData) {
        return;
      }

      dispatch(setLoading(true));

      try {
        const [totalResponse, filteredTotalResponse, dataResponse] = await Promise.all([
          helpHttp().get(ENDPOINT, 'department/getinformationtabledepartment', token),
          helpHttp().post(ENDPOINT, 'department/getcountfilterdepartment', token, applyFiltersDepartment),
          helpHttp().get3(ENDPOINT, 'department/departments', token, currencyOffset, LIMIT_PAGE, applyFiltersDepartment),
        ]);

        if (ignore) {
          return;
        }

        const hasConnectionError = (response) => response?.err || response?.message === 'Load failed';
        const hasAuthError = (response) => response?.err;

        if (hasConnectionError(totalResponse) || hasConnectionError(filteredTotalResponse) || hasConnectionError(dataResponse)) {
          setIsLoadingErrorDepartment(true);
          setDataDepartment(INITIAL_DATA);
          dispatch(
            warningErrorAction(
              t(dataResponse?.message === 'Load failed' ? 'Text_ConnectionFailied' : hasAuthError(dataResponse) ? 'Text_InvalidCredentials' : 'Text_IncorrectData'),
            ),
          );
          return;
        }

        setNumTotRegDepartment(totalResponse);
        setNumTotRegFilter(filteredTotalResponse);
        setDataDepartment(dataResponse);
        setIsLoadingErrorDepartment(false);
      } catch (error) {
        if (!ignore) {
          setIsLoadingErrorDepartment(true);
          setDataDepartment(INITIAL_DATA);
          dispatch(warningErrorAction(t('Text_IncorrectData')));
        }
      } finally {
        if (!ignore) {
          dispatch(setLoading(false));
          setRefreshData(false);
        }
      }
    };

    fetchAll();

    return () => {
      ignore = true;
    };
  }, [applyFiltersDepartment, currencyOffset, dispatch, refreshData, showDepartmentSettings, t, token]);

  useEffect(() => {
    let cancelled = false;

    const syncEditForm = async () => {
      if (!openEditRowDepartment) {
        setFormDepartment(EMPTY_DEPARTMENT);
        setFiles([]);
        return;
      }

      setFormDepartment({
        id: rowEditDepartment.id ?? '',
        code: rowEditDepartment.code ?? '',
        namees: rowEditDepartment.namees ?? '',
        nameen: rowEditDepartment.nameen ?? '',
        filename: rowEditDepartment.filename ?? '',
        createdate: rowEditDepartment.createdate ?? null,
        datafile: rowEditDepartment.datafile ?? '',
        type: rowEditDepartment.type ?? '',
        numTotSections: rowEditDepartment.numTotSections ?? 0,
        sections: rowEditDepartment.sections ?? [],
      });

      const mappedFiles = await departmentToFileObject(rowEditDepartment);
      if (!cancelled) {
        setFiles(mappedFiles);
      }
    };

    syncEditForm();

    return () => {
      cancelled = true;
    };
  }, [openEditRowDepartment, rowEditDepartment]);

  if (!showDepartmentSettings) {
    return null;
  }

  return (
    <div className="container-fluid formdi-pending-page">
      <div id="myDIV" className="formdi-pending-header">
        <h1 className="formdi-pending-title">
          {isLoadingErrorDepartment ? (
            <i className="fas fa-solid fa-exclamation fa-2x text-danger" />
          ) : isLoadingData ? (
            <CircularProgress color={openDeleteRowDepartment ? 'warning' : 'primary'} />
          ) : (
            <i className="fas fa-store text-warning" />
          )}{' '}
          {t('Text_Department_Config')}
        </h1>

        <div>
          {permissions.canCreate && (
            <button
              type="button"
              className="formdi-pending-btn formdi-pending-btn--primary"
              onClick={openCreateOrEditDataDepartment}
            >
              {t('Button_Create')}
            </button>
          )}
          {permissions.canCreate && <span>&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;</span>}
          <button
            type="button"
            className="formdi-pending-btn formdi-pending-btn--secondary"
            onClick={closeModalDepartmentTypeSettings}
          >
            {t('Button_Back')}
          </button>
        </div>
      </div>

      <br />

      <div className="formdi-pending-search">
        <TextField
          id="outlined-basic"
          variant="outlined"
          value={inputTextDepartment}
          placeholder={t('Text_Department_Search')}
          onChange={inputHandlerDepartment}
          fullWidth
          label={t('Text_Department_Search')}
        />
      </div>

      <br />

      <div id="myNewDIV" className="formdi-pending-filters">
        <h1 className="formdi-pending-filter-title">{t('Text_Filter')}</h1>
        <div className="formdi-pending-filters-row">
          <button
            type="button"
            className={`formdi-pending-chip ${applyFiltersDepartment.idFrom || applyFiltersDepartment.idTo ? 'formdi-pending-chip--active' : ''}`}
            onClick={openEditFiltersDepartment}
          >
            <span
              className={`formdi-pending-dot ${applyFiltersDepartment.idFrom || applyFiltersDepartment.idTo ? 'formdi-pending-dot--on' : ''}`}
            />
            {t('Text_Code')}
          </button>

          <button
            type="button"
            className={`formdi-pending-chip ${applyFiltersDepartment.codeFrom || applyFiltersDepartment.codeTo ? 'formdi-pending-chip--active' : ''}`}
            onClick={openEditFiltersDepartment}
          >
            <span
              className={`formdi-pending-dot ${applyFiltersDepartment.codeFrom || applyFiltersDepartment.codeTo ? 'formdi-pending-dot--on' : ''}`}
            />
            {t('Text_CodeDepartment')}
          </button>

          <button
            type="button"
            className={`formdi-pending-chip ${applyFiltersDepartment.nameesFrom || applyFiltersDepartment.nameesTo ? 'formdi-pending-chip--active' : ''}`}
            onClick={openEditFiltersDepartment}
          >
            <span
              className={`formdi-pending-dot ${applyFiltersDepartment.nameesFrom || applyFiltersDepartment.nameesTo ? 'formdi-pending-dot--on' : ''}`}
            />
            {t('Text_NameEs')}
          </button>

          <button
            type="button"
            className={`formdi-pending-chip ${applyFiltersDepartment.nameenFrom || applyFiltersDepartment.nameenTo ? 'formdi-pending-chip--active' : ''}`}
            onClick={openEditFiltersDepartment}
          >
            <span
              className={`formdi-pending-dot ${applyFiltersDepartment.nameenFrom || applyFiltersDepartment.nameenTo ? 'formdi-pending-dot--on' : ''}`}
            />
            {t('Text_NameEn')}
          </button>
        </div>
      </div>

      <br />

      <div className="formdi-pending-card-flat">
        <div className="formdi-pending-card-content">
          <TableSortingDepartment
            dataDepartment={filteredDataDepartment}
            headCellsDepartment={HEAD_CELLS}
            numTotRegFilter={numTotRegFilter}
            titleTable={t('Text_Department_Table')}
            setRowEditDepartment={setRowEditDepartment}
            setOpenEditRowDepartment={setOpenEditRowDepartment}
            setOpenDeleteRowDepartment={setOpenDeleteRowDepartment}
            setOpenSections={setOpenSections}
            showEditButton={permissions.canEdit}
            showDeleteButton={permissions.canDelete}
            showVisibilityButton={permissions.canViewSections}
            numTotRegDepartment={numTotRegDepartment}
            activeOffset={activeOffset}
            rangeOffset={rangeOffset}
            currencyOffset={currencyOffset}
            setCurrencyOffset={setCurrencyOffset}
            minvalue={Number(NON_ERASABLE_DEPARTMENT_ID)}
          />
        </div>
      </div>

      {openEditFilterDepartment && (
        <SwipeableDrawer
          anchor="right"
          open
          onClose={toggleDrawerFilterEditDepartment(false)}
          onOpen={toggleDrawerFilterEditDepartment(true)}
        >
          <Container>
            <Box sx={{ bgcolor: '#ffffff', height: '100vh', width: '500px' }}>
              <br />
              <div id="myDIV">
                <h1 className="h1NewStyle px-4 mb-2">{t('Text_Filter')}</h1>
              </div>
              <hr className="sidebar-divider d-none d-md-block" />

              <form className="createfilter" onSubmit={(e) => e.preventDefault()}>
                <div className="form-floating mb-3 px-4 primary">
                  {[
                    ['idFrom', 'Text_Filter_Code_From', 'PlaceHolder_Filter_Code_From', 15],
                    ['idTo', 'Text_Filter_Code_To', 'PlaceHolder_Filter_Code_to', 15],
                    ['codeFrom', 'Text_Filter_Department_CodeFrom', 'PlaceHolderr_Department_CodeFrom', 100],
                    ['codeTo', 'Text_Filter_Department_CodeTo', 'PlaceHolder_Department_CodeTo', 100],
                    ['nameesFrom', 'Text_Filter_NameEs_From', 'PlaceHolder_Filter_NameEs_From', 100],
                    ['nameesTo', 'Text_Filter_NameEs_To', 'PlaceHolder_Filter_NameEs_to', 100],
                    ['nameenFrom', 'Text_Filter_NameEn_From', 'PlaceHolder_Filter_NameEn_From', 100],
                    ['nameenTo', 'Text_Filter_NameEn_To', 'PlaceHolder_Filter_NameEn_to', 100],
                  ].map(([name, labelKey, placeholderKey, maxLength]) => (
                    <React.Fragment key={name}>
                      <label className="text-primary" style={{ fontSize: 18 }}>
                        {t(labelKey)} :
                      </label>
                      <div className="form-group">
                        <TextField
                          id={name}
                          name={name}
                          value={filtersDepartment[name]}
                          inputProps={{ style: { width: 450, fontSize: '18px' }, maxLength }}
                          InputLabelProps={{ style: { fontSize: '18px', background: '#FFFFFF' } }}
                          label={errorsFiltersDepartment[name] ? t(errorsFiltersDepartment[name]) : t(placeholderKey)}
                          placeholder={t(placeholderKey)}
                          variant="standard"
                          onBlur={handleBlurFilterEditDepartment}
                          onChange={handleChangeFilterEditDepartment}
                        />
                      </div>
                    </React.Fragment>
                  ))}

                  <hr className="sidebar-divider d-none d-md-block" />
                  <div className="formdi-pending-action-group">
                    <button
                      className="formdi-pending-action-btn formdi-pending-action-btn--primary"
                      type="button"
                      onClick={handleSubmitFilterEditDepartment}
                    >
                      {t('Button_Apply')}
                    </button>
                    <button
                      className="formdi-pending-action-btn formdi-pending-action-btn--secondary"
                      type="button"
                      onClick={handleDeleteFiltersDepartment}
                    >
                      {t('Button_Cancel_Filters')}
                    </button>
                  </div>
                </div>
              </form>
            </Box>
          </Container>
        </SwipeableDrawer>
      )}

      {(openCreateDepartment || openEditRowDepartment) && (
        <SwipeableDrawer
          anchor="right"
          open
          onClose={toggleDrawerDataEditDepartment(false)}
          onOpen={toggleDrawerDataEditDepartment(true)}
        >
          <Container>
            <Box sx={{ bgcolor: '#ffffff', height: '100vh', width: '500px' }}>
              <br />
              <div id="myDIV">
                <h1 className="h1NewStyle px-4 mb-2">
                  {openEditRowDepartment ? t('Text_Department_Edit') : t('Text_Department_Create')}
                </h1>
              </div>
              <hr className="sidebar-divider d-none d-md-block" />

              <form className="createDepartment" onSubmit={(e) => e.preventDefault()}>
                <div className="form-floating mb-3 px-4 primary">
                  <label className="text-primary" style={{ fontSize: 18 }}>
                    {t('Text_Code')} :
                  </label>
                  <div className="form-group">
                    <TextField
                      id="id"
                      name="id"
                      value={formDepartment.id}
                      inputProps={{ style: { width: 200, fontSize: '18px' }, maxLength: 15 }}
                      InputLabelProps={{ style: { fontSize: '18px', background: '#FFFFFF' } }}
                      label={t('PlaceHolder_Country_Code')}
                      placeholder={t('PlaceHolder_Country_Code')}
                      variant="standard"
                      disabled
                    />
                  </div>

                  <label className="text-primary" style={{ fontSize: 18 }}>
                    {t('Text_CodeDepartment')} :
                  </label>
                  <div className="form-group">
                    <TextField
                      id="code"
                      name="code"
                      value={formDepartment.code}
                      inputProps={{ style: { width: 450, fontSize: '18px' }, maxLength: 10 }}
                      InputLabelProps={{ style: { fontSize: '18px', background: '#FFFFFF' } }}
                      label={errorsDepartment.code ? t(errorsDepartment.code) : t('Placeholder_CodeDepartment')}
                      placeholder={t('Placeholder_CodeDepartment')}
                      variant="standard"
                      onBlur={handleBlurDepartment}
                      onChange={handleChangeDepartment}
                    />
                  </div>

                  <label className="text-primary" style={{ fontSize: 18 }}>
                    {t('Text_NameEs')} :
                  </label>
                  <div className="form-group">
                    <TextField
                      id="namees"
                      name="namees"
                      value={formDepartment.namees}
                      inputProps={{ style: { width: 450, fontSize: '18px' }, maxLength: 100 }}
                      InputLabelProps={{ style: { fontSize: '18px', background: '#FFFFFF' } }}
                      label={errorsDepartment.namees ? t(errorsDepartment.namees) : t('PlaceHolder_Department_NameEs')}
                      placeholder={t('PlaceHolder_Department_NameEs')}
                      variant="standard"
                      onBlur={handleBlurDepartment}
                      onChange={handleChangeDepartment}
                    />
                  </div>

                  <label className="text-primary" style={{ fontSize: 18 }}>
                    {t('Text_NameEn')} :
                  </label>
                  <div className="form-group">
                    <TextField
                      id="nameen"
                      name="nameen"
                      value={formDepartment.nameen}
                      inputProps={{ style: { width: 450, fontSize: '18px' }, maxLength: 100 }}
                      InputLabelProps={{ style: { fontSize: '18px', background: '#FFFFFF' } }}
                      label={errorsDepartment.nameen ? t(errorsDepartment.nameen) : t('PlaceHolder_Department_NameEn')}
                      placeholder={t('PlaceHolder_Department_NameEn')}
                      variant="standard"
                      onBlur={handleBlurDepartment}
                      onChange={handleChangeDepartment}
                    />
                  </div>

                  <br />
                  <br />

                  <div className="upload-box" style={{ width: '100%' }}>
                    <DropzoneAreaBase
                      initialFiles={files}
                      fileObjects={files}
                      acceptedFiles={['image/*']}
                      maxFileSize={20000000}
                      filesLimit={1}
                      onAdd={handleAddFileImage}
                      onDelete={handleDeleteFileImage}
                      showAlerts={false}
                      dropzoneText={t('Text_Select_Image')}
                      showPreviews={false}
                      showPreviewsInDropzone={false}
                      showFileNamesInPreview={false}
                      showFileNames={false}
                      useChipsForPreview
                      previewGridProps={{ container: { spacing: 1, direction: 'row' } }}
                      previewChipProps={{ classes: { root: 'upload-preview-chip' } }}
                      classes={{
                        root: 'upload-dropzone-root',
                        icon: 'upload-dropzone-icon',
                        text: 'upload-dropzone-text',
                      }}
                    />

                    {files.length > 0 && (
                      <div className="preview-thumbnails-wrapper">
                        <div className="preview-thumbnails">
                          {files.map((fileObj, index) => (
                            <div key={index} className="thumbnail-container">
                              <img src={fileObj.data} alt={`preview-${index}`} className="thumbnail-image" />
                              <button
                                type="button"
                                onClick={() => handleDeleteFileImage(fileObj)}
                                className="thumbnail-delete-btn"
                                title={t('Text_Delete')}
                              >
                                &times;
                              </button>
                              &nbsp;&nbsp;
                              <Chip
                                value={fileObj.file.name}
                                label={fileObj.file.name}
                                clickable={false}
                                style={{ color: '#e74a3b', fontSize: 14 }}
                              />
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>

                  <hr className="sidebar-divider d-none d-md-block" />
                  <div className="formdi-pending-action-group">
                    <button
                      className="formdi-pending-action-btn formdi-pending-action-btn--primary"
                      type="button"
                      onClick={handleSubmitDataEditDepartment}
                    >
                      {openEditRowDepartment ? t('Button_Update') : t('Button_Create')}
                    </button>
                    <button
                      className="formdi-pending-action-btn formdi-pending-action-btn--secondary"
                      type="button"
                      onClick={handleCloseDataEditDepartment}
                    >
                      {t('Button_Cancel')}
                    </button>
                  </div>
                </div>
              </form>
            </Box>
          </Container>
        </SwipeableDrawer>
      )}

      <SectionSettings
        openSections={openSections}
        setOpenSections={setOpenSections}
        rowEditDepartment={rowEditDepartment}
      />

      <Dialog
        open={openDeleteRowDepartment}
        onClose={handleCancelDeleteRowDepartment}
        PaperProps={{ className: 'my-dialog-paper-danger' }}
      >
        <DialogTitle className="my-dialog-title-danger">
          {t('Text_Delete')} {rowEditDepartment.id} - {rowEditDepartment.code} -{' '}
          {i18n.language === 'es' ? rowEditDepartment.namees : rowEditDepartment.nameen}
        </DialogTitle>

        <DialogContent>
          <DialogContentText className="my-dialog-contentText-danger">
            {t('Text_Line1')}
            <br />
            {t('Text_Line2')}
          </DialogContentText>

          <TextField
            autoFocus
            margin="dense"
            id="idCode"
            label={t('Text_Label')}
            type="text"
            fullWidth
            variant="standard"
            className="my-dialog-input-danger"
            value={idCodeDelete}
            onChange={handleChangeDeleteRowDepartment}
          />
        </DialogContent>

        <DialogActions className="my-dialog-actions">
          <Button onClick={handleCancelDeleteRowDepartment} className="my-dialog-button-cancel-danger">
            {t('Button_Cancel')}
          </Button>
          <Button variant="contained" onClick={handleCloseDeleteRowDepartment} className="my-dialog-button-delete">
            {t('Button_Delete')}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

DepartmentSettings.propTypes = {
  setShowPrincipalMenu: PropTypes.func.isRequired,
  setShowModalGeneralSettings: PropTypes.func.isRequired,
  showDepartmentSettings: PropTypes.bool.isRequired,
  setShowModalDepartmentSettings: PropTypes.func.isRequired,
  setShowModalArticlesSettings: PropTypes.func.isRequired,
  isArticle: PropTypes.bool.isRequired,
  setShowModalPurchasesDeliverySettings: PropTypes.func.isRequired,
  isPurchasesDelivery: PropTypes.bool.isRequired,
};

export default DepartmentSettings;
